import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

const ONBOARDING_DRAFT_KEY = 'izio.onboarding.draft'

export function saveOnboardingDraft(draft: any) {
  try {
    localStorage.setItem(ONBOARDING_DRAFT_KEY, JSON.stringify(draft))
  } catch (e) {
    console.error('Failed to save draft', e)
  }
}

export function loadOnboardingDraft() {
  try {
    const draft = localStorage.getItem(ONBOARDING_DRAFT_KEY)
    return draft ? JSON.parse(draft) : null
  } catch (e) {
    console.error('Failed to load draft', e)
    return null
  }
}

export function clearOnboardingDraft() {
  try {
    localStorage.removeItem(ONBOARDING_DRAFT_KEY)
  } catch (e) {
    console.error('Failed to clear draft', e)
  }
}

const LAST_LOGIN_METHOD_KEY = 'izio.auth.lastMethod'

export type LastLoginMethod = 'facebook' | 'google' | 'email'

export function getLastLoginMethod(): LastLoginMethod | null {
  try {
    const v = localStorage.getItem(LAST_LOGIN_METHOD_KEY)
    if (v === 'facebook' || v === 'google' || v === 'email') return v
    return null
  } catch {
    return null
  }
}

export function setLastLoginMethod(method: LastLoginMethod) {
  try {
    localStorage.setItem(LAST_LOGIN_METHOD_KEY, method)
  } catch (e) {
    console.error('Failed to save last login method', e)
  }
}

// --- Session (mock auth) ---

export type UserRole = 'demand' | 'supply'

// Profile status for demand users — replaces public/private semantics
// 'active' = looking, visible in marketplace
// 'inactive' = paused, hidden from marketplace; profile still accessible by direct link with banner
export type ProfileStatus = 'active' | 'inactive'

export interface Session {
  name: string
  email?: string
  phone?: string
  avatar?: string
  role: UserRole
  hasListing?: boolean
  profileStatus?: ProfileStatus
  // Supply-only fields (landlord/agent profile)
  subRole?: 'proprietar' | 'agent' // null/undefined triggers sub-role selection nudge
  agencyName?: string // only when subRole === 'agent'
  website?: string // optional, especially for agencies
  bio?: string // free text about landlord/agent
  slug?: string // for share links; derive on the fly if absent
  // Social links (shared across roles)
  linkedinUrl?: string
  facebookUrl?: string
  // Year user joined the platform (for "Pe Izio din [an]" line)
  memberSince?: number
}

const SESSION_KEY = 'izio.auth.session'

export function getSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    return raw ? (JSON.parse(raw) as Session) : null
  } catch {
    return null
  }
}

export function setSession(session: Session) {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session))
  } catch (e) {
    console.error('Failed to save session', e)
  }
}

export function updateSession(patch: Partial<Session>) {
  const current = getSession()
  if (!current) return
  setSession({ ...current, ...patch })
}

export function clearSession() {
  try {
    localStorage.removeItem(SESSION_KEY)
  } catch (e) {
    console.error('Failed to clear session', e)
  }
}

// --- Display helpers ---

// Currency: EUR only across the platform. Show only the max budget — keeps cards uncluttered.
export function formatBudget(maxEur: number): string {
  return `${maxEur.toLocaleString('ro-RO')} €`
}

// Living mode label — re-export for non-onboarding contexts (display)
export type LivingModeValue = 'single' | 'couple' | 'family' | 'combo'
export const LIVING_MODE_DISPLAY: Record<LivingModeValue, string> = {
  single: 'Singur(ă)',
  couple: 'Cuplu',
  family: 'Familie',
  combo: 'Co-living',
}

// --- Bookmarks (saved tenant profiles) ---

export interface BookmarkedProfile {
  slug: string
  firstName: string
  lastName: string
  occupation: string
  city: string
  neighborhoods?: string
  profilePhoto?: string
  isVerified?: boolean
  budgetMin: number
  budgetMax: number
  budgetCurrency: 'EUR' | 'RON'
  savedAt: number
  status?: 'active' | 'inactive'
}

const BOOKMARKS_KEY = 'izio.bookmarks'

export function getBookmarks(): BookmarkedProfile[] {
  try {
    const raw = localStorage.getItem(BOOKMARKS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export function isBookmarked(slug: string): boolean {
  return getBookmarks().some((b) => b.slug === slug)
}

export function toggleBookmark(
  profile: Omit<BookmarkedProfile, 'savedAt'>,
): boolean {
  // Returns new bookmarked state (true = now saved, false = now removed)
  const current = getBookmarks()
  const existing = current.find((b) => b.slug === profile.slug)
  let next: BookmarkedProfile[]
  let nowSaved: boolean
  if (existing) {
    next = current.filter((b) => b.slug !== profile.slug)
    nowSaved = false
  } else {
    next = [{ ...profile, savedAt: Date.now() }, ...current]
    nowSaved = true
  }
  try {
    localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(next))
  } catch (e) {
    console.error('Failed to save bookmarks', e)
  }
  return nowSaved
}

export function removeBookmark(slug: string) {
  const next = getBookmarks().filter((b) => b.slug !== slug)
  try {
    localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(next))
  } catch (e) {
    console.error('Failed to remove bookmark', e)
  }
}

// --- Recent listing publish (one-shot success banner flag) ---

const RECENT_PUBLISH_KEY = 'izio.listing.recentPublish'

export function setRecentPublish() {
  try {
    localStorage.setItem(RECENT_PUBLISH_KEY, '1')
  } catch (e) {
    console.error('Failed to set recent publish flag', e)
  }
}

export function getRecentPublish(): boolean {
  try {
    return localStorage.getItem(RECENT_PUBLISH_KEY) === '1'
  } catch {
    return false
  }
}

export function clearRecentPublish() {
  try {
    localStorage.removeItem(RECENT_PUBLISH_KEY)
  } catch (e) {
    console.error('Failed to clear recent publish flag', e)
  }
}

// --- Verifications ---

export type VerificationStatus =
  | 'unverified'
  | 'pending'
  | 'verified'
  | 'rejected'
  | 'expired'

export interface VerificationState {
  status: VerificationStatus
  verifiedAt?: string
  expiresAt?: string
  submittedAt?: string
  rejectedAt?: string
  rejectionReason?: string
}

export function toVerificationState(
  v: boolean | VerificationState | undefined,
): VerificationState {
  if (!v) return { status: 'unverified' }
  if (typeof v === 'boolean') return { status: v ? 'verified' : 'unverified' }
  return v
}

const VERIFICATIONS_KEY_PREFIX = 'izio.verifications.'

export function getVerificationStates(
  email: string,
): Record<string, VerificationState> {
  if (typeof window === 'undefined') return {}
  try {
    const raw = localStorage.getItem(`${VERIFICATIONS_KEY_PREFIX}${email}`)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

export function setVerificationState(
  email: string,
  type: 'email' | 'identity' | 'income' | 'business',
  state: VerificationState,
) {
  if (typeof window === 'undefined') return
  try {
    const current = getVerificationStates(email)
    current[type] = state
    localStorage.setItem(
      `${VERIFICATIONS_KEY_PREFIX}${email}`,
      JSON.stringify(current),
    )
  } catch (e) {
    console.error('Failed to save verification state', e)
  }
}

export function isFullyVerified(v?: {
  email: boolean | VerificationState
  identity: boolean | VerificationState
  income: boolean | VerificationState
}): boolean {
  if (!v) return false
  const e = toVerificationState(v.email).status === 'verified'
  const id = toVerificationState(v.identity).status === 'verified'
  const inc = toVerificationState(v.income).status === 'verified'
  return e && id && inc
}

// --- Application Tracking ---

const SENT_APPLICATIONS_KEY = 'izio.sent_applications'

export function getSentApplications(): string[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = localStorage.getItem(SENT_APPLICATIONS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    console.error('Failed to parse sent applications', e)
    return []
  }
}

export function markApplicationSent(slug: string): void {
  if (typeof window === 'undefined') return
  try {
    const apps = getSentApplications()
    if (!apps.includes(slug)) {
      apps.push(slug)
      localStorage.setItem(SENT_APPLICATIONS_KEY, JSON.stringify(apps))
    }
  } catch (e) {
    console.error('Failed to save sent application', e)
  }
}

export function hasAppliedTo(slug: string): boolean {
  return getSentApplications().includes(slug)
}

// --- Offer Tracking ---

export type OfferStatus =
  | 'new'
  | 'postponed'
  | 'saved'
  | 'rejected'
  | 'succes'
  | 'archived'

export function getNewOffersCountForRole(role: UserRole): number {
  // Mock implementation for prototype
  return role === 'demand' ? 2 : 1
}

// --- Offer Store (E2E persistence) ---

const OFFERS_KEY = 'izio_offers_v1'

export interface StoredOffer {
  id: string
  status: OfferStatus
  fromEmail: string
  fromName: string
  fromAvatar?: string
  fromSubRole?: 'proprietar' | 'agent'
  fromAgencyName?: string
  toSlug: string
  toName: string
  sentAt: string
  updatedAt: string
  message: string
  listingPreview?: {
    image?: string
    title: string
    priceLabel: string
    locationLabel: string
    url: string
  }
  photos?: string[]
  contactEmail?: string
  contactPhone?: string
  matchScore?: string
  matchedFeatures?: string[]
  missingFeatures?: string[]
  // L8 additions
  succesAt?: string
  archivedAt?: string
  archiveSessionId?: string
  _preArchiveStatus?: OfferStatus
  // L10 — association with a saved property (auto-saved or selected at send-time)
  propertyId?: string
  // Notes attached to status transitions
  acceptNote?: string
  rejectNote?: string
  // L10.S2 — retraction tracking (supply withdraws an offer pre-acceptance).
  // Sets status='rejected' + rejectNote with the "Retras de" prefix.
  retractedAt?: string
  retractedBy?: 'proprietar' | 'agent'
  // L10.S2 — report flag for the "Ceva nu e în regulă" support flow.
  reportedAt?: string
  reportedReason?: string
}

export function getOffers(): StoredOffer[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = localStorage.getItem(OFFERS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    console.error('Failed to parse offers', e)
    return []
  }
}

function saveOffers(offers: StoredOffer[]) {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(OFFERS_KEY, JSON.stringify(offers))
  } catch (e) {
    console.error('Failed to save offers', e)
  }
}

export function saveOffer(offer: StoredOffer): void {
  const all = getOffers()
  const existing = all.findIndex((o) => o.id === offer.id)
  if (existing >= 0) {
    all[existing] = offer
  } else {
    all.unshift(offer) // new offers at top
  }
  saveOffers(all)
}

export function getOffersFromSupply(email: string): StoredOffer[] {
  return getOffers().filter((o) => o.fromEmail === email)
}

export function getOffersForTenant(slug: string): StoredOffer[] {
  return getOffers().filter((o) => o.toSlug === slug)
}

export function updateOfferStatus(id: string, status: OfferStatus): void {
  const all = getOffers()
  const offer = all.find((o) => o.id === id)
  if (offer) {
    offer.status = status
    offer.updatedAt = new Date().toISOString()
    saveOffers(all)
  }
}

/**
 * L10 — Attach an offer to a saved SavedProperty. Used by the wizard's
 * transparent auto-save so we know which offers came from the same property.
 * Idempotent: silently noops if the offer doesn't exist.
 */
export function tagOfferWithProperty(
  offerId: string,
  propertyId: string,
): void {
  const all = getOffers()
  const offer = all.find((o) => o.id === offerId)
  if (!offer) return
  if (offer.propertyId === propertyId) return
  offer.propertyId = propertyId
  offer.updatedAt = new Date().toISOString()
  saveOffers(all)
}

/**
 * L10 — Find the most recent offer that this landlord sent to this tenant.
 * Used to back-tag the offer with its propertyId once the wizard auto-saves.
 */
export function findLatestOfferFromTo(
  fromEmail: string,
  toSlug: string,
): StoredOffer | null {
  const matching = getOffers().filter(
    (o) => o.fromEmail === fromEmail && o.toSlug === toSlug,
  )
  if (matching.length === 0) return null
  // Sort by updatedAt desc, fall back to createdAt
  const sorted = [...matching].sort((a, b) => {
    const aT = new Date(a.updatedAt || a.createdAt || 0).getTime()
    const bT = new Date(b.updatedAt || b.createdAt || 0).getTime()
    return bT - aT
  })
  return sorted[0]
}

export function hasOfferTo(fromEmail: string, toSlug: string): boolean {
  return getOffers().some(
    (o) => o.fromEmail === fromEmail && o.toSlug === toSlug,
  )
}

export function hasSuccesOfferTo(toSlug: string): boolean {
  return getOffers().some((o) => o.toSlug === toSlug && o.status === 'succes')
}

/**
 * Demo-only mapping: supply profile slug → demo email. Used to derive whether
 * a logged-in tenant can leave a review for the supply profile they're
 * viewing (i.e. they actually completed a rental together). Real platform
 * would resolve this via a proper user table lookup.
 */
const DEMO_SUPPLY_SLUG_TO_EMAIL: Record<string, string> = {
  'andrei-proprietar': 'andrei.proprietar@izio.test',
  'maria-agent': 'maria.agent@izio.test',
  'george-agent-solo': 'george.agent@izio.test',
}

export function supplyEmailFromSlug(slug: string): string | undefined {
  return DEMO_SUPPLY_SLUG_TO_EMAIL[slug]
}

/** Reverse lookup for the demo slug→email map (used to jump from an offer's
 *  fromEmail to the supply's public profile slug). */
export function supplySlugFromEmail(email?: string): string | undefined {
  if (!email) return undefined
  return Object.keys(DEMO_SUPPLY_SLUG_TO_EMAIL).find(
    (slug) => DEMO_SUPPLY_SLUG_TO_EMAIL[slug] === email,
  )
}

// ─── Review persistence ─────────────────────────────────────────────────────
//
// Reviews submitted through ReviewSubmission persist in localStorage so they
// surface on the subject's profile right after publication. Mock seeds in
// LandlordProfile / TenantProfile coexist with the stored set — readers merge
// both lists. Production swaps this with a real reviews API.

const REVIEWS_KEY = 'izio_reviews_v1'

export interface StoredReview {
  id: string
  subjectType: 'tenant' | 'landlord' | 'agent'
  /** Slug of the user being reviewed (supply slug for landlord/agent, tenant slug for tenant). */
  subjectSlug: string
  /** Slug of the reviewer when known (logged-in reviewer). */
  reviewerSlug?: string
  reviewerName: string
  reviewerEmail?: string
  reviewerAvatar?: string
  reviewerRole: string
  rating: number
  categoryRatings: Record<string, number>
  body: string
  date: string
  createdAt: string
  status: 'transactional' | 'verified_by_document' | 'pending' | 'unverified'
  reviewerVerified?: boolean
}

export function getStoredReviews(): StoredReview[] {
  try {
    const raw = localStorage.getItem(REVIEWS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function saveStoredReviewsList(list: StoredReview[]): void {
  try {
    localStorage.setItem(REVIEWS_KEY, JSON.stringify(list))
  } catch {
    /* localStorage unavailable — fail silently */
  }
}

export function saveStoredReview(review: StoredReview): void {
  const list = getStoredReviews()
  // De-dupe by id, replace if same id resubmitted.
  const filtered = list.filter((r) => r.id !== review.id)
  filtered.push(review)
  saveStoredReviewsList(filtered)
}

/** Returns all stored reviews about a subject (supply or tenant). Newest first. */
export function getStoredReviewsForSubject(slug: string): StoredReview[] {
  if (!slug) return []
  return getStoredReviews()
    .filter((r) => r.subjectSlug === slug)
    .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))
}

/** Has this reviewer already written a review about this subject? */
export function hasReviewedSubject(
  reviewerSlug: string | undefined,
  subjectSlug: string | undefined,
): boolean {
  if (!reviewerSlug || !subjectSlug) return false
  return getStoredReviews().some(
    (r) => r.reviewerSlug === reviewerSlug && r.subjectSlug === subjectSlug,
  )
}

/**
 * Returns true when the demo store contains a `succes` offer FROM the supply
 * identified by `supplySlug` TO the tenant identified by `tenantSlug` — the
 * canonical signal that "this tenant rented from this supply" and therefore
 * is allowed to leave a review on the supply profile.
 */
export function hasCompletedRentalBetween(
  supplySlug: string,
  tenantSlug: string,
): boolean {
  const email = supplyEmailFromSlug(supplySlug)
  if (!email || !tenantSlug) return false
  return getOffers().some(
    (o) =>
      o.fromEmail === email && o.toSlug === tenantSlug && o.status === 'succes',
  )
}

// --- L8.1: Anunț history (single source of truth for closed anunțuri) ---

export type AnuntHistoryStatus = 'succes' | 'archived'

export interface AnuntHistoryEntry {
  id: string
  slug: string
  status: AnuntHistoryStatus
  closedAt: string
  // Counterparty info (succes only) — denormalized so we don't have to
  // join against StoredOffer at render time.
  succesOfferId?: string
  succesCounterpartyName?: string
  succesCounterpartyAvatar?: string
  succesCounterpartySubRole?: 'proprietar' | 'agent'
  succesCounterpartyAgencyName?: string
  succesListingTitle?: string
  // Frozen snapshot of the anunț at the moment of closure.
  anuntSnapshot: {
    budgetMaxEur: number
    housingType: string
    rooms: string
    floor: string
    yearBuiltLabel: string
    neighborhoods: string
    moveInDate: string
    contractDuration: string
    livingMode: string
    isSmoker: boolean
    pets: string | null
    searchBio?: string
    amenities: string[]
  }
  // Offer ids that were associated with this closed anunț (for succes).
  associatedOfferIds: string[]
}

const ANUNT_HISTORY_KEY = 'izio.anunt_history_v1'

export function getAnuntHistory(): AnuntHistoryEntry[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = localStorage.getItem(ANUNT_HISTORY_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function saveAnuntHistory(entries: AnuntHistoryEntry[]): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(ANUNT_HISTORY_KEY, JSON.stringify(entries))
  } catch (e) {
    console.error('Failed to save anunt history', e)
  }
}

export function appendAnuntHistory(entry: AnuntHistoryEntry): void {
  const all = getAnuntHistory()
  // Replace existing entry with same id if present
  const filtered = all.filter((e) => e.id !== entry.id)
  saveAnuntHistory([entry, ...filtered])
}

export function removeAnuntHistory(id: string): void {
  const all = getAnuntHistory()
  saveAnuntHistory(all.filter((e) => e.id !== id))
}

export function getAnuntHistoryEntry(id: string): AnuntHistoryEntry | null {
  return getAnuntHistory().find((e) => e.id === id) || null
}

// --- L8: Archive sessions, offer progress, anunț finalization ---

export interface ArchiveSession {
  id: string
  year: number
  name: string
  closedAt: string
  succesOfferId?: string
  externalRental: boolean
  offerIds: string[]
  anuntSnapshot?: {
    slug: string
    neighborhoods?: string
    budgetMaxEur?: number
    moveInDate?: string
  }
}

const ARCHIVE_SESSIONS_KEY = 'izio.archive_sessions_v1'
const OFFER_PROGRESS_KEY = 'izio.offer_progress_v1'
const ANUNT_FINALIZED_KEY = 'izio.anunt_finalized_v1'

export interface OfferProgress {
  whatsappSent: boolean
  viewingScheduled: boolean
  contractSigned: boolean
  paymentMade: boolean
  movedIn: boolean
}

const EMPTY_OFFER_PROGRESS: OfferProgress = {
  whatsappSent: false,
  viewingScheduled: false,
  contractSigned: false,
  paymentMade: false,
  movedIn: false,
}

export function getOfferProgress(offerId: string): OfferProgress {
  if (typeof window === 'undefined') return { ...EMPTY_OFFER_PROGRESS }
  try {
    const raw = localStorage.getItem(OFFER_PROGRESS_KEY)
    if (!raw) return { ...EMPTY_OFFER_PROGRESS }
    const all = JSON.parse(raw) as Record<string, Partial<OfferProgress>>
    return { ...EMPTY_OFFER_PROGRESS, ...(all[offerId] || {}) }
  } catch {
    return { ...EMPTY_OFFER_PROGRESS }
  }
}

export function setOfferProgress(
  offerId: string,
  patch: Partial<OfferProgress>,
): void {
  if (typeof window === 'undefined') return
  try {
    const raw = localStorage.getItem(OFFER_PROGRESS_KEY)
    const all: Record<string, OfferProgress> = raw ? JSON.parse(raw) : {}
    all[offerId] = {
      ...EMPTY_OFFER_PROGRESS,
      ...(all[offerId] || {}),
      ...patch,
    }
    localStorage.setItem(OFFER_PROGRESS_KEY, JSON.stringify(all))
  } catch (e) {
    console.error('Failed to save offer progress', e)
  }
}

export function clearOfferProgress(offerId: string): void {
  if (typeof window === 'undefined') return
  try {
    const raw = localStorage.getItem(OFFER_PROGRESS_KEY)
    if (!raw) return
    const all = JSON.parse(raw) as Record<string, OfferProgress>
    delete all[offerId]
    localStorage.setItem(OFFER_PROGRESS_KEY, JSON.stringify(all))
  } catch (e) {
    console.error('Failed to clear offer progress', e)
  }
}

/**
 * Full 5-toggle gate matching the 6-step checklist (item 1 — "Vezi datele
 * de contact" — is implicit because saved offers already expose contact).
 */
export function isOfferChecklistComplete(offerId: string): boolean {
  const p = getOfferProgress(offerId)
  return (
    p.whatsappSent &&
    p.viewingScheduled &&
    p.contractSigned &&
    p.paymentMade &&
    p.movedIn
  )
}

export function getOfferProgressCount(offerId: string): {
  done: number
  total: number
} {
  const p = getOfferProgress(offerId)
  // Item 1 (Vezi datele de contact) is implicit-done for saved offers.
  const done =
    1 +
    (p.whatsappSent ? 1 : 0) +
    (p.viewingScheduled ? 1 : 0) +
    (p.contractSigned ? 1 : 0) +
    (p.paymentMade ? 1 : 0) +
    (p.movedIn ? 1 : 0)
  return { done, total: 6 }
}

export function getArchiveSessions(): ArchiveSession[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = localStorage.getItem(ARCHIVE_SESSIONS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function saveArchiveSessions(sessions: ArchiveSession[]): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(ARCHIVE_SESSIONS_KEY, JSON.stringify(sessions))
  } catch (e) {
    console.error('Failed to save archive sessions', e)
  }
}

export interface AnuntFinalization {
  slug: string
  finalized: boolean
  succesOfferId?: string
  succesAt?: string
}

export function getAnuntFinalization(): AnuntFinalization | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(ANUNT_FINALIZED_KEY)
    return raw ? (JSON.parse(raw) as AnuntFinalization) : null
  } catch {
    return null
  }
}

export function setAnuntFinalization(state: AnuntFinalization): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(ANUNT_FINALIZED_KEY, JSON.stringify(state))
  } catch (e) {
    console.error('Failed to save anunt finalization', e)
  }
}

export function clearAnuntFinalization(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(ANUNT_FINALIZED_KEY)
  } catch (e) {
    console.error('Failed to clear anunt finalization', e)
  }
}

// L8.2 — Dismiss the SuccesCelebration banner without undoing the finalization.
// The history entry stays; the active slot becomes empty so the user can
// create a new anunț. Tracked per-session-id in localStorage.
const SUCCES_DISMISSED_KEY = 'izio.succes_dismissed_v1'

export function isSuccesDismissed(sessionId?: string): boolean {
  if (typeof window === 'undefined' || !sessionId) return false
  try {
    const raw = localStorage.getItem(SUCCES_DISMISSED_KEY)
    if (!raw) return false
    const arr = JSON.parse(raw) as string[]
    return Array.isArray(arr) && arr.includes(sessionId)
  } catch {
    return false
  }
}

export function dismissSucces(sessionId: string): void {
  if (typeof window === 'undefined') return
  try {
    const raw = localStorage.getItem(SUCCES_DISMISSED_KEY)
    const arr: string[] = raw ? JSON.parse(raw) : []
    if (!arr.includes(sessionId)) {
      arr.push(sessionId)
      localStorage.setItem(SUCCES_DISMISSED_KEY, JSON.stringify(arr))
    }
  } catch (e) {
    console.error('Failed to dismiss succes', e)
  }
}

// L8.4 — Landlord succes-acknowledged tracking. When a landlord's outgoing
// offer transitions to 'succes', we surface a celebratory Callout on
// DashboardAplicari until they dismiss it. Dismissal is persisted by
// offer id so it doesn't reappear on every page load.
const SUCCES_SEEN_KEY = 'izio.succes_seen_v1'

export function getSeenSuccesIds(): string[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = localStorage.getItem(SUCCES_SEEN_KEY)
    if (!raw) return []
    const arr = JSON.parse(raw)
    return Array.isArray(arr) ? arr : []
  } catch {
    return []
  }
}

export function markSuccesSeen(offerIds: string[]): void {
  if (typeof window === 'undefined' || offerIds.length === 0) return
  try {
    const existing = getSeenSuccesIds()
    const merged = Array.from(new Set([...existing, ...offerIds]))
    localStorage.setItem(SUCCES_SEEN_KEY, JSON.stringify(merged))
  } catch (e) {
    console.error('Failed to persist succes-seen ids', e)
  }
}

// Count of truly-new offers (excludes succes/archived) for sidebar badge.
export function getRealNewOffersCount(args: {
  role: UserRole
  email?: string
  slug?: string
}): number {
  const all = getOffers()
  if (args.role === 'demand') {
    if (!args.slug) return 0
    return all.filter((o) => o.toSlug === args.slug && o.status === 'new')
      .length
  }
  if (!args.email) return 0
  return all.filter((o) => o.fromEmail === args.email && o.status === 'new')
    .length
}

/**
 * Mark a saved offer as succes. Creates a new ArchiveSession for the current
 * year, flips all other offers belonging to the same tenant slug to 'archived',
 * and writes the anunt_finalized record.
 */
export function markOfferAsSucces(
  offerId: string,
  anuntSnapshot?: ArchiveSession['anuntSnapshot'],
  fullAnuntSnapshot?: AnuntHistoryEntry['anuntSnapshot'],
): ArchiveSession | null {
  const all = getOffers()
  const target = all.find((o) => o.id === offerId)
  if (!target) return null

  const now = new Date()
  const sessionId = `sess_${now.getFullYear()}_${Math.random()
    .toString(36)
    .slice(2, 8)}`
  const session: ArchiveSession = {
    id: sessionId,
    year: now.getFullYear(),
    name: `Sesiunea ${now.getFullYear()}`,
    closedAt: now.toISOString(),
    succesOfferId: offerId,
    externalRental: false,
    offerIds: [],
    anuntSnapshot,
  }

  const tenantSlug = target.toSlug
  const winningPropertyId = target.propertyId
  const winningFromEmail = target.fromEmail
  const updated = all.map((o) => {
    // Demand-side archive: every other live offer belonging to THIS tenant
    if (o.toSlug === tenantSlug) {
      if (o.id === offerId) {
        session.offerIds.push(o.id)
        return {
          ...o,
          status: 'succes' as OfferStatus,
          succesAt: now.toISOString(),
          updatedAt: now.toISOString(),
          archiveSessionId: sessionId,
        }
      }
      if (o.status !== 'archived' && o.status !== 'succes') {
        session.offerIds.push(o.id)
        return {
          ...o,
          _preArchiveStatus: o.status,
          status: 'archived' as OfferStatus,
          archivedAt: now.toISOString(),
          updatedAt: now.toISOString(),
          archiveSessionId: sessionId,
        }
      }
      return o
    }
    // L10 — Supply-side property archive: when a landlord's offer wins on a
    // specific saved property, any other live offers from the same landlord
    // tied to the same property (i.e. sent to OTHER tenants for the same
    // apartment) are auto-archived so they're not still pitching a unit that
    // has just been rented out.
    if (
      winningPropertyId &&
      o.fromEmail === winningFromEmail &&
      o.propertyId === winningPropertyId &&
      o.status !== 'archived' &&
      o.status !== 'succes'
    ) {
      session.offerIds.push(o.id)
      return {
        ...o,
        _preArchiveStatus: o.status,
        status: 'archived' as OfferStatus,
        archivedAt: now.toISOString(),
        updatedAt: now.toISOString(),
        archiveSessionId: sessionId,
      }
    }
    return o
  })

  try {
    localStorage.setItem(OFFERS_KEY, JSON.stringify(updated))
  } catch (e) {
    console.error('Failed to persist offers after succes', e)
  }

  const sessions = getArchiveSessions()
  saveArchiveSessions([session, ...sessions])

  setAnuntFinalization({
    slug: tenantSlug,
    finalized: true,
    succesOfferId: offerId,
    succesAt: now.toISOString(),
  })

  // L8.1 — also write to the unified anunț history
  if (fullAnuntSnapshot) {
    appendAnuntHistory({
      id: sessionId,
      slug: tenantSlug,
      status: 'succes',
      closedAt: now.toISOString(),
      succesOfferId: offerId,
      succesCounterpartyName: target.fromName,
      succesCounterpartyAvatar: target.fromAvatar,
      succesCounterpartySubRole: target.fromSubRole,
      succesCounterpartyAgencyName: target.fromAgencyName,
      succesListingTitle: target.listingPreview?.title,
      anuntSnapshot: fullAnuntSnapshot,
      associatedOfferIds: session.offerIds,
    })
  }

  return session
}

/**
 * Manual archive — user shelves the anunț without claiming a rental. NO modal,
 * NO offer archiving, NO succes flow. Just moves the anunț into history and
 * sets profileStatus to inactive.
 */
export function archiveCurrentAnuntManually(
  slug: string,
  anuntSnapshot: AnuntHistoryEntry['anuntSnapshot'],
): AnuntHistoryEntry {
  const now = new Date()
  const id = `arch_${now.getFullYear()}_${Math.random()
    .toString(36)
    .slice(2, 8)}`
  const entry: AnuntHistoryEntry = {
    id,
    slug,
    status: 'archived',
    closedAt: now.toISOString(),
    anuntSnapshot,
    associatedOfferIds: [],
  }
  appendAnuntHistory(entry)
  updateSession({ profileStatus: 'inactive' })
  return entry
}

/**
 * Step 2b path — user rented externally. Sets profileStatus to inactive but
 * does NOT create an archive session and does NOT touch offers.
 */
export function finalizeAnuntExternally(): void {
  updateSession({ profileStatus: 'inactive' })
}

/**
 * Reverse markOfferAsSucces. Restores all offers in the latest session back to
 * their _preArchiveStatus and deletes the session. Used by 'Anulează finalizarea'.
 */
export function cancelFinalization(): void {
  const finalization = getAnuntFinalization()
  if (!finalization?.finalized) return

  const sessions = getArchiveSessions()
  const session = sessions.find(
    (s) => s.succesOfferId === finalization.succesOfferId,
  )
  if (!session) {
    clearAnuntFinalization()
    return
  }

  const all = getOffers()
  const restored = all.map((o) => {
    if (!session.offerIds.includes(o.id)) return o
    const prev = o._preArchiveStatus || 'saved'
    const next: StoredOffer = {
      ...o,
      status: o.id === session.succesOfferId ? 'saved' : prev,
      archiveSessionId: undefined,
      archivedAt: undefined,
      succesAt: undefined,
      _preArchiveStatus: undefined,
      updatedAt: new Date().toISOString(),
    }
    return next
  })

  try {
    localStorage.setItem(OFFERS_KEY, JSON.stringify(restored))
  } catch (e) {
    console.error('Failed to restore offers', e)
  }

  saveArchiveSessions(sessions.filter((s) => s.id !== session.id))
  clearAnuntFinalization()
}

// --- Combo-rent helpers ---

export interface ComboMember {
  id: string
  name: string
  avatar?: string
}

export interface ComboJoinRequest {
  id: string
  name: string
  avatar?: string
  age?: number
  occupation?: string
  email?: string
  phone?: string
  appliedAt: string // human-readable, e.g. "acum 2 zile"
  slug?: string // for profile click-through
}

/**
 * Sub-line label for a finalized combo group — shown under the listing
 * creator's name in place of the usual occupation. The header name itself
 * ALWAYS remains the creator's name (we never rename the card to "Combo"
 * or "Grup").
 *
 * MVP semantics (invite-only groups):
 * - 2 members → "Combo" (two friends sharing — not a couple)
 * - 3+ members → "Grup de N" (where N = total members, including creator)
 * - 1 member → null (regular solo listing, occupation shows)
 *
 * The matchmaking / "looking for colegi" states are deferred for MVP, so
 * there's no longer an "open" or "still seeking" variant.
 */
export function comboGroupLabel(memberCount: number): string | null {
  if (memberCount === 2) return 'Combo'
  if (memberCount >= 3) return `Grup de ${memberCount}`
  return null
}

// --- Pending Intent (auth-gate return flow) ---
//
// When an unauthenticated user clicks a gated action (e.g. "Trimite ofertă"),
// we stash the intent here before redirecting to Login/Register. After auth
// succeeds, Login/Register consume the intent and route the user back to the
// original tenant anunț with `openApplyModal: true` in the navigation context.

export type PendingIntentType = 'send_offer' | 'bookmark'

export interface PendingIntent {
  type: PendingIntentType
  tenantSlug: string
  createdAt: number
}

const PENDING_INTENT_KEY = 'izio.pending_intent'
const PENDING_INTENT_TTL_MS = 1000 * 60 * 30 // 30 minutes

export function setPendingIntent(
  intent: Omit<PendingIntent, 'createdAt'>,
): void {
  if (typeof window === 'undefined') return
  try {
    const payload: PendingIntent = { ...intent, createdAt: Date.now() }
    localStorage.setItem(PENDING_INTENT_KEY, JSON.stringify(payload))
  } catch (e) {
    console.error('Failed to save pending intent', e)
  }
}

export function getPendingIntent(): PendingIntent | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(PENDING_INTENT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PendingIntent
    if (!parsed || !parsed.tenantSlug || !parsed.type) return null
    // Expire stale intents so old sessions don't surprise the user.
    if (Date.now() - parsed.createdAt > PENDING_INTENT_TTL_MS) {
      localStorage.removeItem(PENDING_INTENT_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function clearPendingIntent(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(PENDING_INTENT_KEY)
  } catch (e) {
    console.error('Failed to clear pending intent', e)
  }
}

// --- L9: Saved properties (landlord/agent reusable property templates) ---
//
// Landlords and agents can save the properties they manage so they can
// reuse them as templates when sending offers. Each user owns their own
// list, scoped by email. Properties have a manual active/inactive flag
// — purely a visual flag, doesn't block reuse.

export interface SavedPropertyFacilities {
  location: string[]
  essential: string[]
  preferred: string[]
}

export interface SavedProperty {
  id: string
  ownerEmail: string
  name: string
  locationLabel?: string
  priceLabel?: string
  link?: string
  thumbnail?: string
  facilities: SavedPropertyFacilities
  isActive: boolean
  verificationStatus?: VerificationState
  title?: string
  photos?: string[]
  housingType?: string
  rooms?: string
  floor?: string
  constructionYear?: string
  city?: string
  neighborhood?: string
  priceEur?: number
  contractDuration?: string
  petsAllowed?: boolean
  smokingAllowed?: boolean
  description?: string
  presentationMode?: 'link' | 'photos'
  createdAt?: string
  // Supply-side rental closure ritual. When a landlord marks a property as
  // rented (via the supply MovedInModal mirror), we flip rentalStatus to
  // 'rented' and stamp the closure metadata. This hides the property from
  // the active property switcher in DashboardAplicari and surfaces it in
  // the "Proprietăți închiriate" history on SupplyProfileView.
  rentalStatus?: 'active' | 'rented'
  rentedAt?: string
  rentedTenantSlug?: string
  rentedTenantName?: string
  rentedOfferId?: string
}

const SAVED_PROPERTIES_KEY = 'izio.saved_properties_v1'

type PropertiesStore = Record<string, SavedProperty[]>

function readPropertiesStore(): PropertiesStore {
  if (typeof window === 'undefined') return {}
  try {
    const raw = localStorage.getItem(SAVED_PROPERTIES_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function writePropertiesStore(store: PropertiesStore): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(SAVED_PROPERTIES_KEY, JSON.stringify(store))
  } catch (e) {
    console.error('Failed to save properties store', e)
  }
}

export function getMyProperties(email?: string): SavedProperty[] {
  if (!email) return []
  const store = readPropertiesStore()
  const list = store[email]
  return Array.isArray(list) ? list : []
}

/**
 * Active properties = not rented out. Used by the property switcher in
 * DashboardAplicari and "Proprietățile mele" on SupplyProfileView so the
 * landlord only sees what's still in play.
 */
export function getActiveProperties(email?: string): SavedProperty[] {
  return getMyProperties(email).filter(
    (p) => (p.rentalStatus || 'active') !== 'rented',
  )
}

/**
 * Closed sessions — properties the landlord successfully rented out.
 * Surfaced in the "Proprietăți închiriate" history section.
 */
export function getRentedProperties(email?: string): SavedProperty[] {
  return getMyProperties(email)
    .filter((p) => p.rentalStatus === 'rented')
    .sort((a, b) => {
      const aT = a.rentedAt ? new Date(a.rentedAt).getTime() : 0
      const bT = b.rentedAt ? new Date(b.rentedAt).getTime() : 0
      return bT - aT
    })
}

/**
 * Supply-side rental closure ritual. When the landlord confirms a property
 * has been rented out:
 *  - flip the property to rentalStatus='rented' + stamp closure metadata
 *  - mark it inactive so it disappears from the active switcher
 *  - flip the winning offer to 'succes'
 *  - archive every other live offer the landlord sent that's tied to the
 *    same propertyId (they were pitching the same apartment)
 * Returns the updated property, or null if not found.
 */
export function markPropertyAsRented(args: {
  propertyId: string
  offerId: string
  tenantSlug: string
  tenantName: string
}): SavedProperty | null {
  const { propertyId, offerId, tenantSlug, tenantName } = args
  const now = new Date().toISOString()

  // 1) Flip the property
  const updated = updateProperty(propertyId, {
    rentalStatus: 'rented',
    rentedAt: now,
    rentedTenantSlug: tenantSlug,
    rentedTenantName: tenantName,
    rentedOfferId: offerId,
    isActive: false,
  })
  if (!updated) return null

  // 2) Flip the offers
  const all = getOffers()
  const winningFromEmail = all.find((o) => o.id === offerId)?.fromEmail
  const next = all.map((o) => {
    if (o.id === offerId) {
      return {
        ...o,
        status: 'succes' as OfferStatus,
        succesAt: now,
        updatedAt: now,
      }
    }
    if (
      winningFromEmail &&
      o.fromEmail === winningFromEmail &&
      o.propertyId === propertyId &&
      o.status !== 'archived' &&
      o.status !== 'succes'
    ) {
      return {
        ...o,
        _preArchiveStatus: o.status,
        status: 'archived' as OfferStatus,
        archivedAt: now,
        updatedAt: now,
      }
    }
    return o
  })
  try {
    localStorage.setItem(OFFERS_KEY, JSON.stringify(next))
  } catch (e) {
    console.error('Failed to persist offers after property rental', e)
  }

  return updated
}

// ─────────────────────────────────────────────────────────────────
// L10.S2 — Supply-side offer retraction + archive listing + report
// ─────────────────────────────────────────────────────────────────

/**
 * Retract a sent offer (supply action). Flips the offer to `rejected` with
 * a standardized rejectNote that the demand side renders as "Retrasă de
 * proprietar/agent". Clears the renter's checklist progress for this offer.
 */
export function retractOffer(
  offerId: string,
  retractedBy: 'proprietar' | 'agent',
): StoredOffer | null {
  const now = new Date().toISOString()
  const all = getOffers()
  let retracted: StoredOffer | null = null
  const next = all.map((o) => {
    if (o.id !== offerId) return o
    retracted = {
      ...o,
      _preArchiveStatus: o.status,
      status: 'rejected' as OfferStatus,
      rejectNote: `Retras de ${retractedBy === 'agent' ? 'agent' : 'proprietar'}`,
      retractedAt: now,
      retractedBy,
      updatedAt: now,
    }
    return retracted
  })
  if (!retracted) return null
  try {
    localStorage.setItem(OFFERS_KEY, JSON.stringify(next))
  } catch (e) {
    console.error('Failed to persist retracted offer', e)
  }
  // Wipe the renter checklist for this offer.
  clearOfferProgress(offerId)
  return retracted
}

/**
 * Mark a succes offer as reported (the "Ceva nu e în regulă" path). Pure
 * mock — sets metadata without changing state.
 */
export function reportRentalIssue(
  offerId: string,
  reason: string,
  details?: string,
): StoredOffer | null {
  const now = new Date().toISOString()
  const all = getOffers()
  let updated: StoredOffer | null = null
  const next = all.map((o) => {
    if (o.id !== offerId) return o
    updated = {
      ...o,
      reportedAt: now,
      reportedReason: details ? `${reason} — ${details}` : reason,
      updatedAt: now,
    }
    return updated
  })
  if (!updated) return null
  try {
    localStorage.setItem(OFFERS_KEY, JSON.stringify(next))
  } catch (e) {
    console.error('Failed to persist rental issue report', e)
  }
  return updated
}

/**
 * Archive item types for the supply-side Arhivă page. A closure groups
 * one rented property with all its sibling archived offers. A retraction
 * is a single offer the landlord withdrew.
 */
export type SupplyArchiveItem =
  | {
      kind: 'closure'
      property: SavedProperty
      succesOffer?: StoredOffer
      siblingOffers: StoredOffer[]
      closedAt: string
    }
  | {
      kind: 'retracted'
      offer: StoredOffer
      retractedAt: string
    }

/**
 * Build the supply-side archive list — closure-induced archives grouped
 * by property + standalone retracted offers. Sorted newest first.
 */
export function getArchiveListForSupply(email?: string): SupplyArchiveItem[] {
  if (!email) return []
  const properties = getMyProperties(email).filter(
    (p) => p.rentalStatus === 'rented',
  )
  const all = getOffers()
  const myOffers = all.filter((o) => o.fromEmail === email)

  const closures: SupplyArchiveItem[] = properties.map((p) => {
    const succesOffer = p.rentedOfferId
      ? myOffers.find((o) => o.id === p.rentedOfferId)
      : myOffers.find(
          (o) =>
            o.status === 'succes' &&
            (o.propertyId === p.id ||
              o.listingPreview?.title?.toLowerCase().trim() ===
                (p.title || (p as any).name || '').toLowerCase().trim()),
        )
    const propTitle = (p.title || (p as any).name || '').toLowerCase().trim()
    const siblingOffers = myOffers.filter((o) => {
      if (o.id === succesOffer?.id) return false
      if (o.status !== 'archived') return false
      if (o.propertyId && o.propertyId === p.id) return true
      const t = o.listingPreview?.title?.toLowerCase().trim()
      if (!t || !propTitle) return false
      return t === propTitle || t.includes(propTitle) || propTitle.includes(t)
    })
    return {
      kind: 'closure' as const,
      property: p,
      succesOffer,
      siblingOffers,
      closedAt: p.rentedAt || succesOffer?.succesAt || p.createdAt || '',
    }
  })

  // Retracted offers: status===rejected + retractedAt set. Skip ones already
  // grouped under a closure (in case a retracted offer was later archived).
  const retracted: SupplyArchiveItem[] = myOffers
    .filter((o) => o.status === 'rejected' && o.retractedAt)
    .map((o) => ({
      kind: 'retracted' as const,
      offer: o,
      retractedAt: o.retractedAt!,
    }))

  return [...closures, ...retracted].sort((a, b) => {
    const ad = a.kind === 'closure' ? a.closedAt : a.retractedAt
    const bd = b.kind === 'closure' ? b.closedAt : b.retractedAt
    return (bd || '').localeCompare(ad || '')
  })
}

/**
 * Count total archived items for the supply-side header badge: archived
 * sibling offers across all rented properties + retracted offers.
 */
export function getSupplyArchiveCount(email?: string): number {
  const items = getArchiveListForSupply(email)
  return items.reduce(
    (acc, it) => acc + (it.kind === 'closure' ? it.siblingOffers.length : 1),
    0,
  )
}

/**
 * Unconfirmed successes for the supply — offers in 'succes' status whose
 * property has NOT yet been marked as rented. These power the Callout
 * (single / stack / condensed).
 */
export function getUnconfirmedSuccesses(email?: string): Array<{
  offer: StoredOffer
  property: SavedProperty | null
}> {
  if (!email) return []
  const all = getOffers()
  const properties = getMyProperties(email)
  return all
    .filter((o) => o.fromEmail === email && o.status === 'succes')
    .map((o) => {
      // Match by propertyId first, fall back to title match.
      let property: SavedProperty | null = null
      if (o.propertyId) {
        property = properties.find((p) => p.id === o.propertyId) || null
      }
      if (!property) {
        const t = o.listingPreview?.title?.toLowerCase().trim()
        if (t) {
          property =
            properties.find(
              (p) =>
                (p.title || (p as any).name || '').toLowerCase().trim() === t,
            ) || null
        }
      }
      return { offer: o, property }
    })
    .filter(
      ({ property }) =>
        // Only show successes that haven't been confirmed (i.e. property
        // either doesn't exist as a saved entry, or hasn't flipped to
        // rented yet).
        !property || property.rentalStatus !== 'rented',
    )
}

export function saveProperty(
  property: Omit<SavedProperty, 'id' | 'createdAt'> & {
    id?: string
    createdAt?: string
  },
): SavedProperty {
  const store = readPropertiesStore()
  const list = store[property.ownerEmail] || []
  const entry: SavedProperty = {
    id:
      property.id ||
      `prop_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
    createdAt: property.createdAt || new Date().toISOString(),
    ownerEmail: property.ownerEmail,
    name: property.name,
    title: property.title,
    priceLabel: property.priceLabel,
    locationLabel: property.locationLabel,
    link: property.link,
    photos: property.photos,
    thumbnail: property.thumbnail,
    facilities: property.facilities,
    isActive: property.isActive,
    verificationStatus: property.verificationStatus,
    housingType: property.housingType,
    rooms: property.rooms,
    floor: property.floor,
    constructionYear: property.constructionYear,
    city: property.city,
    neighborhood: property.neighborhood,
    priceEur: property.priceEur,
    contractDuration: property.contractDuration,
    petsAllowed: property.petsAllowed,
    smokingAllowed: property.smokingAllowed,
    description: property.description,
    presentationMode: property.presentationMode,
    rentalStatus: property.rentalStatus || 'active',
    rentedAt: property.rentedAt,
    rentedTenantSlug: property.rentedTenantSlug,
    rentedTenantName: property.rentedTenantName,
    rentedOfferId: property.rentedOfferId,
  }
  store[property.ownerEmail] = [entry, ...list]
  writePropertiesStore(store)
  return entry
}

export function updateProperty(
  id: string,
  patch: Partial<Omit<SavedProperty, 'id' | 'ownerEmail' | 'createdAt'>>,
): SavedProperty | null {
  const store = readPropertiesStore()
  let updated: SavedProperty | null = null
  for (const email of Object.keys(store)) {
    const list = store[email]
    const idx = list.findIndex((p) => p.id === id)
    if (idx >= 0) {
      list[idx] = { ...list[idx], ...patch }
      updated = list[idx]
      break
    }
  }
  if (updated) writePropertiesStore(store)
  return updated
}

export function deleteProperty(id: string): boolean {
  const store = readPropertiesStore()
  let removed = false
  for (const email of Object.keys(store)) {
    const before = store[email].length
    store[email] = store[email].filter((p) => p.id !== id)
    if (store[email].length !== before) removed = true
  }
  if (removed) writePropertiesStore(store)
  return removed
}

export function togglePropertyActive(id: string): SavedProperty | null {
  const store = readPropertiesStore()
  for (const email of Object.keys(store)) {
    const list = store[email]
    const idx = list.findIndex((p) => p.id === id)
    if (idx >= 0) {
      list[idx] = { ...list[idx], isActive: !list[idx].isActive }
      writePropertiesStore(store)
      return list[idx]
    }
  }
  return null
}

// L10 — Property lookup and dedup helpers.

export function getPropertyById(id: string): SavedProperty | null {
  if (typeof window === 'undefined') return null
  const store = readPropertiesStore()
  for (const email of Object.keys(store)) {
    const found = store[email].find((p) => p.id === id)
    if (found) return found
  }
  return null
}

function normalizeName(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

export function computeFacilitySignature(
  facilities: SavedPropertyFacilities,
  priceLabel?: string,
): string {
  const all = [
    ...facilities.location,
    ...facilities.essential,
    ...facilities.preferred,
  ]
    .map((s) => s.trim())
    .filter(Boolean)
    .sort()
  return `${all.join('|')}::${(priceLabel || '').trim()}`
}

export interface FindOrCreateResult {
  property: SavedProperty
  matchType: 'name' | 'signature' | 'new'
}

/**
 * Look up an existing property for the owner that matches either by
 * normalized name or by facility+price signature. If found, return it
 * untouched; otherwise create a new one. Never throws.
 */
export function findOrCreatePropertyByFacilities(
  ownerEmail: string,
  facilities: SavedPropertyFacilities,
  defaults: {
    name: string
    priceLabel?: string
    locationLabel?: string
    link?: string
    photos?: string[]
    thumbnail?: string
  },
): FindOrCreateResult {
  const existing = getMyProperties(ownerEmail)
  const normalizedTarget = normalizeName(defaults.name)
  const targetSignature = computeFacilitySignature(
    facilities,
    defaults.priceLabel,
  )

  // 1) Match by normalized name
  const byName = existing.find(
    (p) =>
      normalizeName(p.name) === normalizedTarget && normalizedTarget !== '',
  )
  if (byName) return { property: byName, matchType: 'name' }

  // 2) Match by facility+price signature
  const bySignature = existing.find(
    (p) =>
      computeFacilitySignature(p.facilities, p.priceLabel) === targetSignature,
  )
  if (bySignature) return { property: bySignature, matchType: 'signature' }

  // 3) Create new
  const created = saveProperty({
    ownerEmail,
    name: defaults.name,
    title: defaults.name,
    priceLabel: defaults.priceLabel,
    locationLabel: defaults.locationLabel,
    link: defaults.link,
    photos: defaults.photos,
    thumbnail: defaults.thumbnail,
    facilities,
    isActive: true,
  })
  return { property: created, matchType: 'new' }
}

// --- L8 demo seeding ---

function clearL8DemoState(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(OFFERS_KEY)
    localStorage.removeItem(ARCHIVE_SESSIONS_KEY)
    localStorage.removeItem(ANUNT_FINALIZED_KEY)
    localStorage.removeItem(OFFER_PROGRESS_KEY)
    localStorage.removeItem(ANUNT_HISTORY_KEY)
    localStorage.removeItem(SAVED_PROPERTIES_KEY)
  } catch (e) {
    console.error('Failed to clear L8 demo state', e)
  }
}

export function seedL8DemoFor(email?: string): void {
  if (typeof window === 'undefined') return
  clearL8DemoState()
  if (!email) return

  const now = new Date()
  const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
  const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000)

  switch (email) {
    case 'olivia.p@izio.test': {
      // Completed cycle: 1 succes + 2 archived offers, sess_2025 archive, finalization record.
      const sessionId = 'sess_2025_olivia'
      const succesOfferId = 'offer_olivia_succes'
      const archived1 = 'offer_olivia_arch_1'
      const archived2 = 'offer_olivia_arch_2'
      const tenantSlug = 'olivia-p'

      const offers: StoredOffer[] = [
        {
          id: succesOfferId,
          fromEmail: 'andrei.proprietar@izio.test',
          fromName: 'Vlad Constantin',
          fromAvatar: 'https://i.pravatar.cc/150?img=12',
          fromSubRole: 'proprietar',
          toSlug: tenantSlug,
          toName: 'Olivia P.',
          status: 'succes',
          sentAt: twoWeeksAgo.toISOString(),
          updatedAt: lastWeek.toISOString(),
          succesAt: lastWeek.toISOString(),
          archiveSessionId: sessionId,
          message:
            'Bună Olivia! Am un apartament de 3 camere în Floreasca care s-ar potrivi perfect pentru voi și pisica voastră. Centrală, balcon, parcare.',
          contactEmail: 'andrei.proprietar@izio.test',
          contactPhone: '+40 721 123 456',
          matchScore: '7/7',
          matchedFeatures: [
            'Centrală termică',
            'Parcare',
            'Aer condiționat',
            'Mobilat complet',
            'Balcon / Terasă',
            'Lift',
            'Mașină de spălat vase',
          ],
          missingFeatures: [],
          listingPreview: {
            image:
              'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&q=80&w=400',
            title: 'Apartament 3 camere Floreasca, 78 mp',
            priceLabel: '620 €',
            locationLabel: 'Floreasca, București',
            url: 'https://izio.ro/anunt/apartament-3-camere-floreasca',
          },
        },
        {
          id: archived1,
          fromEmail: 'cristian.d@email.ro',
          fromName: 'Cristian D.',
          fromAvatar: 'https://i.pravatar.cc/150?img=15',
          toSlug: tenantSlug,
          toName: 'Olivia P.',
          status: 'archived',
          _preArchiveStatus: 'saved',
          sentAt: twoWeeksAgo.toISOString(),
          updatedAt: lastWeek.toISOString(),
          archivedAt: lastWeek.toISOString(),
          archiveSessionId: sessionId,
          message:
            'Salut! Am un studio modern în Băneasa, 65 mp, complet mobilat. Te-ar interesa o vizionare?',
          contactEmail: 'cristian.d@email.ro',
          contactPhone: '+40 745 678 901',
          matchScore: '5/7',
          matchedFeatures: [
            'Centrală termică',
            'Aer condiționat',
            'Mobilat complet',
            'Balcon / Terasă',
            'Lift',
          ],
          missingFeatures: ['Parcare', 'Mașină de spălat vase'],
          listingPreview: {
            image:
              'https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&q=80&w=400',
            title: 'Studio modern Băneasa',
            priceLabel: '580 €',
            locationLabel: 'Băneasa, București',
            url: 'https://izio.ro/anunt/studio-baneasa',
          },
        },
        {
          id: archived2,
          fromEmail: 'maria.stanescu@email.ro',
          fromName: 'Maria Stănescu',
          fromAvatar: 'https://i.pravatar.cc/150?img=5',
          toSlug: tenantSlug,
          toName: 'Olivia P.',
          status: 'archived',
          _preArchiveStatus: 'new',
          sentAt: twoWeeksAgo.toISOString(),
          updatedAt: lastWeek.toISOString(),
          archivedAt: lastWeek.toISOString(),
          archiveSessionId: sessionId,
          message:
            'Bună Olivia, am un apartament în Aviatorilor. Hai să vorbim dacă mai cauți.',
          contactEmail: 'maria.s@email.ro',
          matchScore: '4/7',
          matchedFeatures: [
            'Centrală termică',
            'Aer condiționat',
            'Mobilat complet',
            'Lift',
          ],
          missingFeatures: [
            'Parcare',
            'Balcon / Terasă',
            'Mașină de spălat vase',
          ],
          listingPreview: {
            image:
              'https://images.unsplash.com/photo-1554995207-c18c203602cb?auto=format&fit=crop&q=80&w=400',
            title: 'Apartament 2 camere Aviatorilor',
            priceLabel: '700 €',
            locationLabel: 'Aviatorilor, București',
            url: 'https://izio.ro/anunt/ap-2-camere-aviatorilor',
          },
        },
      ]

      const session: ArchiveSession = {
        id: sessionId,
        year: lastWeek.getFullYear(),
        name: `Sesiunea ${lastWeek.getFullYear()}`,
        closedAt: lastWeek.toISOString(),
        succesOfferId,
        externalRental: false,
        offerIds: [succesOfferId, archived1, archived2],
        anuntSnapshot: {
          slug: tenantSlug,
          neighborhoods: 'Floreasca, Aviatorilor',
          budgetMaxEur: 650,
          moveInDate: 'Octombrie, 2026',
        },
      }

      // L8.1 — anunț history entry so Olivia's "Istoric anunțuri" zone
      // populates with the closed session out of the box.
      const historyEntry: AnuntHistoryEntry = {
        id: sessionId,
        slug: tenantSlug,
        status: 'succes',
        closedAt: lastWeek.toISOString(),
        succesOfferId,
        succesCounterpartyName: 'Vlad Constantin',
        succesCounterpartyAvatar: 'https://i.pravatar.cc/150?img=12',
        succesCounterpartySubRole: 'proprietar',
        succesListingTitle: 'Apartament 3 camere Floreasca, 78 mp',
        anuntSnapshot: {
          budgetMaxEur: 650,
          housingType: 'apartament_bloc',
          rooms: '3 cam',
          floor: 'Etaj 1-3',
          yearBuiltLabel: '> 1990',
          neighborhoods: 'Floreasca, Aviatorilor',
          moveInDate: 'Octombrie, 2026',
          contractDuration: '1 an',
          livingMode: 'Cuplu',
          isSmoker: false,
          pets: '1 pisică',
          searchBio:
            'Suntem un cuplu liniștit, lucrăm amândoi de acasă. Căutăm un apartament luminos cu balcon și spațiu pentru birou. Avem o pisică foarte educată.',
          amenities: [
            'Centrală termică',
            'Parcare',
            'Aer condiționat',
            'Mobilat complet',
            'Balcon / Terasă',
            'Lift',
            'Mașină de spălat vase',
          ],
        },
        associatedOfferIds: [succesOfferId, archived1, archived2],
      }

      try {
        localStorage.setItem(OFFERS_KEY, JSON.stringify(offers))
        localStorage.setItem(ARCHIVE_SESSIONS_KEY, JSON.stringify([session]))
        localStorage.setItem(
          ANUNT_FINALIZED_KEY,
          JSON.stringify({
            slug: tenantSlug,
            finalized: true,
            succesOfferId,
            succesAt: lastWeek.toISOString(),
          }),
        )
        localStorage.setItem(ANUNT_HISTORY_KEY, JSON.stringify([historyEntry]))
        localStorage.setItem(
          OFFER_PROGRESS_KEY,
          JSON.stringify({
            [succesOfferId]: {
              whatsappSent: true,
              viewingScheduled: true,
              contractSigned: true,
              paymentMade: true,
              movedIn: true,
            },
          }),
        )
      } catch (e) {
        console.error('Failed to seed Olivia demo', e)
      }
      return
    }

    case 'andrei.p@izio.test': {
      // L8.2 — mid-flow demo: Andrei has an active anunț with live offers
      // (2 new + 1 saved). The saved offer can be marked as succes to drive
      // the full closure flow end-to-end.
      // L8.3 — also seeds 1 past closure so Istoric anunțuri is visible
      // out of the box on his profile too.
      const tenantSlug = 'andrei-popescu'
      const fiveDaysAgo = new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000)
      const twoDaysAgo = new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000)
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000)
      const oneYearAgoAndrei = new Date(
        now.getTime() - 365 * 24 * 60 * 60 * 1000,
      )
      const oneYearAgoMinusWeekAndrei = new Date(
        oneYearAgoAndrei.getTime() - 7 * 24 * 60 * 60 * 1000,
      )
      const pastAndreiSessionId = 'sess_2025_andrei'
      const pastAndreiSuccesId = 'offer_andrei_past_succes'
      const offers: StoredOffer[] = [
        // Past closed session — 1 succes offer kept in store so the
        // snapshot view can render it from Istoric anunțuri.
        {
          id: pastAndreiSuccesId,
          fromEmail: 'gabriel.iancu@email.ro',
          fromName: 'Gabriel Iancu',
          fromAvatar: 'https://i.pravatar.cc/150?img=58',
          fromSubRole: 'proprietar',
          toSlug: tenantSlug,
          toName: 'Andrei P.',
          status: 'succes',
          sentAt: oneYearAgoMinusWeekAndrei.toISOString(),
          updatedAt: oneYearAgoAndrei.toISOString(),
          succesAt: oneYearAgoAndrei.toISOString(),
          archiveSessionId: pastAndreiSessionId,
          message:
            'Salut Andrei! Garsoniera mea din Drumul Taberei e exact ce-ți trebuie pentru anul de internship.',
          contactEmail: 'gabriel.i@email.ro',
          contactPhone: '+40 733 444 555',
          matchScore: '4/5',
          listingPreview: {
            image:
              'https://images.unsplash.com/photo-1554995207-c18c203602cb?auto=format&fit=crop&q=80&w=400',
            title: 'Garsonieră Drumul Taberei',
            priceLabel: '380 €',
            locationLabel: 'Drumul Taberei, București',
            url: 'https://izio.ro/anunt/gars-drumul-taberei',
          },
        },
        // Current cycle — 3 live offers

        {
          id: 'offer_andrei_saved_1',
          fromEmail: 'radu.popescu@email.ro',
          fromName: 'Radu Popescu',
          fromAvatar: 'https://i.pravatar.cc/150?img=33',
          fromSubRole: 'proprietar',
          toSlug: tenantSlug,
          toName: 'Andrei P.',
          status: 'saved',
          sentAt: fiveDaysAgo.toISOString(),
          updatedAt: twoDaysAgo.toISOString(),
          message:
            'Salut Andrei! Apartamentul meu de 2 camere din Cotroceni s-ar potrivi cu ce cauți — etaj 3, recent renovat, lângă metrou.',
          contactEmail: 'radu.popescu@email.ro',
          contactPhone: '+40 722 555 111',
          matchScore: '6/7',
          matchedFeatures: [
            'Aer condiționat',
            'Centrală termică',
            'Balcon / Terasă',
            'Mobilat complet',
          ],
          missingFeatures: ['Parcare'],
          listingPreview: {
            image:
              'https://images.unsplash.com/photo-1502672260266-1c1e52d15461?auto=format&fit=crop&q=80&w=400',
            title: 'Apartament 2 camere Cotroceni',
            priceLabel: '530 €',
            locationLabel: 'Cotroceni, București',
            url: 'https://izio.ro/anunt/ap-cotroceni',
          },
        },
        {
          id: 'offer_andrei_new_1',
          fromEmail: 'elena.dumitrescu@email.ro',
          fromName: 'Elena Dumitrescu',
          fromAvatar: 'https://i.pravatar.cc/150?img=44',
          fromSubRole: 'agent',
          fromAgencyName: 'Premier Imobiliare',
          toSlug: tenantSlug,
          toName: 'Andrei P.',
          status: 'new',
          sentAt: yesterday.toISOString(),
          updatedAt: yesterday.toISOString(),
          message:
            'Bună Andrei! Am un apartament în Militari, foarte luminos, exact în bugetul tău. Sunt agent Premier.',
          contactEmail: 'elena.d@premier.ro',
          matchScore: '5/7',
          matchedFeatures: [
            'Aer condiționat',
            'Centrală termică',
            'Mobilat complet',
          ],
          missingFeatures: ['Balcon / Terasă', 'Parcare'],
          listingPreview: {
            image:
              'https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&q=80&w=400',
            title: 'Apartament 2 camere Militari',
            priceLabel: '510 €',
            locationLabel: 'Militari, București',
            url: 'https://izio.ro/anunt/ap-militari',
          },
        },
        {
          id: 'offer_andrei_new_2',
          fromEmail: 'dan.ionescu@email.ro',
          fromName: 'Dan Ionescu',
          fromAvatar: 'https://i.pravatar.cc/150?img=60',
          fromSubRole: 'proprietar',
          toSlug: tenantSlug,
          toName: 'Andrei P.',
          status: 'new',
          sentAt: now.toISOString(),
          updatedAt: now.toISOString(),
          message:
            'Salut Andrei, apartamentul meu din Tineretului are parcare și balcon. Te aștept la vizionare.',
          contactEmail: 'dan.ionescu@email.ro',
          matchScore: '7/7',
          matchedFeatures: [
            'Aer condiționat',
            'Centrală termică',
            'Balcon / Terasă',
            'Mobilat complet',
            'Parcare',
          ],
          missingFeatures: [],
          listingPreview: {
            image:
              'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&q=80&w=400',
            title: 'Apartament 2 camere Tineretului',
            priceLabel: '550 €',
            locationLabel: 'Tineretului, București',
            url: 'https://izio.ro/anunt/ap-tineretului',
          },
        },
      ]
      const pastAndreiSession: ArchiveSession = {
        id: pastAndreiSessionId,
        year: oneYearAgoAndrei.getFullYear(),
        name: `Sesiunea ${oneYearAgoAndrei.getFullYear()}`,
        closedAt: oneYearAgoAndrei.toISOString(),
        succesOfferId: pastAndreiSuccesId,
        externalRental: false,
        offerIds: [pastAndreiSuccesId],
        anuntSnapshot: {
          slug: tenantSlug,
          neighborhoods: 'Drumul Taberei',
          budgetMaxEur: 420,
          moveInDate: 'Septembrie, 2025',
        },
      }
      const pastAndreiHistoryEntry: AnuntHistoryEntry = {
        id: pastAndreiSessionId,
        slug: tenantSlug,
        status: 'succes',
        closedAt: oneYearAgoAndrei.toISOString(),
        succesOfferId: pastAndreiSuccesId,
        succesCounterpartyName: 'Gabriel Iancu',
        succesCounterpartyAvatar: 'https://i.pravatar.cc/150?img=58',
        succesCounterpartySubRole: 'proprietar',
        succesListingTitle: 'Garsonieră Drumul Taberei',
        anuntSnapshot: {
          budgetMaxEur: 420,
          housingType: 'apartament_bloc',
          rooms: '1 cam',
          floor: 'Etaj 2-5',
          yearBuiltLabel: '> 1990',
          neighborhoods: 'Drumul Taberei',
          moveInDate: 'Septembrie, 2025',
          contractDuration: '1 an',
          livingMode: 'Singur(ă)',
          isSmoker: false,
          pets: null,
          searchBio: 'Student anul 1, caut o garsonieră aproape de metrou.',
          amenities: ['Mobilat complet', 'Aer condiționat'],
        },
        associatedOfferIds: [pastAndreiSuccesId],
      }

      try {
        localStorage.setItem(OFFERS_KEY, JSON.stringify(offers))
        localStorage.setItem(
          ANUNT_HISTORY_KEY,
          JSON.stringify([pastAndreiHistoryEntry]),
        )
        localStorage.setItem(
          OFFER_PROGRESS_KEY,
          JSON.stringify({
            offer_andrei_saved_1: {
              whatsappSent: true,
              viewingScheduled: true,
              contractSigned: false,
              paymentMade: false,
              movedIn: false,
            },
          }),
        )
      } catch (e) {
        console.error('Failed to seed Andrei demo', e)
      }
      return
    }

    case 'sara.v@izio.test': {
      // L8.2 — veteran demo: Sara has a CURRENT active anunț with no
      // finalization, but her Istoric anunțuri already shows a previously
      // closed rental from a year ago. Lets us demo the "post-history,
      // looking again" state without the FinalizedAnuntCard.
      const tenantSlug = 'combo-pair'
      const oneYearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000)
      const oneYearAgoMinusWeek = new Date(
        oneYearAgo.getTime() - 7 * 24 * 60 * 60 * 1000,
      )
      const pastSessionId = 'sess_2025_sara'
      const pastSuccesOfferId = 'offer_sara_past_succes'
      const pastArchivedId = 'offer_sara_past_archived'

      // Offers: 1 fresh current 'new' offer + 2 from the past closed
      // session (kept in store so the snapshot view can render them).
      const threeDaysAgo = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000)
      const offers: StoredOffer[] = [
        {
          id: 'offer_sara_current_new',
          fromEmail: 'cristian.popa@email.ro',
          fromName: 'Cristian Popa',
          fromAvatar: 'https://i.pravatar.cc/150?img=60',
          fromSubRole: 'proprietar',
          toSlug: tenantSlug,
          toName: 'Sara V.',
          status: 'new',
          sentAt: threeDaysAgo.toISOString(),
          updatedAt: threeDaysAgo.toISOString(),
          message:
            'Salut Sara! Am un apartament de 3 camere în Cotroceni, perfect pentru un grup de 3. Spațios, mobilat, parcare inclusă.',
          contactEmail: 'cristian.p@email.ro',
          contactPhone: '+40 744 888 999',
          matchScore: '4/4',
          matchedFeatures: [
            'Parcare',
            'Mobilat complet',
            'Balcon / Terasă',
            'Lift',
          ],
          missingFeatures: [],
          listingPreview: {
            image:
              'https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&q=80&w=400',
            title: 'Apartament 3 camere Cotroceni',
            priceLabel: '880 €',
            locationLabel: 'Cotroceni, București',
            url: 'https://izio.ro/anunt/ap-cotroceni-3cam',
          },
        },
        {
          id: pastSuccesOfferId,
          fromEmail: 'mihai.tudor@email.ro',
          fromName: 'Mihai Tudor',
          fromAvatar: 'https://i.pravatar.cc/150?img=68',
          fromSubRole: 'proprietar',
          toSlug: tenantSlug,
          toName: 'Sara V.',
          status: 'succes',
          sentAt: oneYearAgoMinusWeek.toISOString(),
          updatedAt: oneYearAgo.toISOString(),
          succesAt: oneYearAgo.toISOString(),
          archiveSessionId: pastSessionId,
          message:
            'Bună Sara! Am un apartament de 3 camere în Floreasca, perfect pentru grupul vostru. 4 camere bune, balcon mare.',
          contactEmail: 'mihai.t@email.ro',
          contactPhone: '+40 733 222 333',
          matchScore: '5/5',
          listingPreview: {
            image:
              'https://images.unsplash.com/photo-1502672260266-1c1e52d15461?auto=format&fit=crop&q=80&w=400',
            title: 'Apartament 3 camere Floreasca',
            priceLabel: '850 €',
            locationLabel: 'Floreasca, București',
            url: 'https://izio.ro/anunt/ap-floreasca-3cam',
          },
        },
        {
          id: pastArchivedId,
          fromEmail: 'andreea.vasilescu@email.ro',
          fromName: 'Andreea Vasilescu',
          fromAvatar: 'https://i.pravatar.cc/150?img=23',
          fromSubRole: 'agent',
          fromAgencyName: 'Re/Max Premium',
          toSlug: tenantSlug,
          toName: 'Sara V.',
          status: 'archived',
          _preArchiveStatus: 'saved',
          sentAt: oneYearAgoMinusWeek.toISOString(),
          updatedAt: oneYearAgo.toISOString(),
          archivedAt: oneYearAgo.toISOString(),
          archiveSessionId: pastSessionId,
          message:
            'Salut! Sunt agent Re/Max. Am un apartament în Centru care s-ar potrivi exact cu cerințele grupului.',
          contactEmail: 'andreea.v@remax.ro',
          matchScore: '4/5',
          listingPreview: {
            image:
              'https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&q=80&w=400',
            title: 'Apartament 3 camere Centru',
            priceLabel: '880 €',
            locationLabel: 'Centru, București',
            url: 'https://izio.ro/anunt/ap-centru-3cam',
          },
        },
      ]

      const pastSession: ArchiveSession = {
        id: pastSessionId,
        year: oneYearAgo.getFullYear(),
        name: `Sesiunea ${oneYearAgo.getFullYear()}`,
        closedAt: oneYearAgo.toISOString(),
        succesOfferId: pastSuccesOfferId,
        externalRental: false,
        offerIds: [pastSuccesOfferId, pastArchivedId],
        anuntSnapshot: {
          slug: tenantSlug,
          neighborhoods: 'Floreasca, Centru',
          budgetMaxEur: 900,
          moveInDate: 'Iulie, 2025',
        },
      }

      const pastHistoryEntry: AnuntHistoryEntry = {
        id: pastSessionId,
        slug: tenantSlug,
        status: 'succes',
        closedAt: oneYearAgo.toISOString(),
        succesOfferId: pastSuccesOfferId,
        succesCounterpartyName: 'Mihai Tudor',
        succesCounterpartyAvatar: 'https://i.pravatar.cc/150?img=68',
        succesCounterpartySubRole: 'proprietar',
        succesListingTitle: 'Apartament 3 camere Floreasca',
        anuntSnapshot: {
          budgetMaxEur: 900,
          housingType: 'apartament_bloc',
          rooms: '3 cam',
          floor: 'Etaj 2-5',
          yearBuiltLabel: '> 2000',
          neighborhoods: 'Floreasca, Centru',
          moveInDate: 'Iulie, 2025',
          contractDuration: '1 an',
          livingMode: 'Co-living',
          isSmoker: false,
          pets: null,
          searchBio:
            'Trei prieteni căutam un apartament de 3 camere pentru anul universitar.',
          amenities: ['Parcare', 'Mobilat complet', 'Balcon / Terasă', 'Lift'],
        },
        associatedOfferIds: [pastSuccesOfferId, pastArchivedId],
      }

      try {
        localStorage.setItem(OFFERS_KEY, JSON.stringify(offers))
        localStorage.setItem(
          ARCHIVE_SESSIONS_KEY,
          JSON.stringify([pastSession]),
        )
        localStorage.setItem(
          ANUNT_HISTORY_KEY,
          JSON.stringify([pastHistoryEntry]),
        )
        // NOTE: no ANUNT_FINALIZED_KEY — current anunț is active, not closed.
      } catch (e) {
        console.error('Failed to seed Sara combo demo', e)
      }
      return
    }

    case 'andrei.proprietar@izio.test': {
      // L9.S1 — Andrei Proprietar demo. 3 active properties + 1 rented
      // (in history) + 6 outgoing offers spread across statuses and
      // properties — gives the property dropdown a meaningful workout.
      const offers: StoredOffer[] = [
        {
          id: 'offer_andrei_succes',
          fromEmail: 'andrei.proprietar@izio.test',
          fromName: 'Andrei Proprietar',
          fromAvatar: 'https://i.pravatar.cc/150?img=33',
          toSlug: 'maria-i',
          toName: 'Maria I.',
          status: 'succes',
          sentAt: twoWeeksAgo.toISOString(),
          updatedAt: lastWeek.toISOString(),
          succesAt: lastWeek.toISOString(),
          message:
            'Bună Maria, apartamentul meu din Dorobanți se potrivește perfect cu ce cauți. Centrală nouă, mobilat, balcon.',
          contactEmail: 'andrei.proprietar@izio.test',
          contactPhone: '+40 712 345 678',
          listingPreview: {
            image:
              'https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&q=80&w=400',
            title: 'Apartament 2 camere Dorobanți',
            priceLabel: '620 €',
            locationLabel: 'Dorobanți, București',
            url: 'https://izio.ro/anunt/ap-2-dorobanti',
          },
        },
        {
          id: 'offer_andrei_olivia',
          fromEmail: 'andrei.proprietar@izio.test',
          fromName: 'Andrei Proprietar',
          fromAvatar: 'https://i.pravatar.cc/150?img=33',
          toSlug: 'olivia-p',
          toName: 'Olivia P.',
          status: 'succes',
          sentAt: lastWeek.toISOString(),
          updatedAt: now.toISOString(),
          succesAt: now.toISOString(),
          propertyId: 'prop_andrei_cotroceni',
          message:
            'Salut Olivia! Am un apartament în Cotroceni care s-ar potrivi cu voi. 5 minute de metrou, complet mobilat.',
          contactEmail: 'andrei.proprietar@izio.test',
          contactPhone: '+40 712 345 678',
          acceptNote: 'Arată super. Vă sun mâine să stabilim vizionare.',
          listingPreview: {
            image:
              'https://images.unsplash.com/photo-1502672260266-1c1e52d15461?auto=format&fit=crop&q=80&w=400',
            title: 'Apartament 2 camere Cotroceni',
            priceLabel: '550 €',
            locationLabel: 'Cotroceni, București',
            url: 'https://izio.ro/anunt/ap-2-cotroceni',
          },
        },
        {
          id: 'offer_andrei_andrei_p',
          fromEmail: 'andrei.proprietar@izio.test',
          fromName: 'Andrei Proprietar',
          fromAvatar: 'https://i.pravatar.cc/150?img=33',
          toSlug: 'andrei-popescu',
          toName: 'Andrei P.',
          status: 'new',
          sentAt: lastWeek.toISOString(),
          updatedAt: lastWeek.toISOString(),
          message:
            'Bună Andrei, am un 3 camere în Floreasca, recent renovat, care cred că ți s-ar potrivi.',
          contactEmail: 'andrei.proprietar@izio.test',
          contactPhone: '+40 712 345 678',
          listingPreview: {
            image:
              'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&q=80&w=400',
            title: 'Apartament 3 camere Floreasca',
            priceLabel: '780 €',
            locationLabel: 'Floreasca, București',
            url: 'https://izio.ro/anunt/ap-3-floreasca',
          },
        },
        {
          id: 'offer_andrei_sara',
          fromEmail: 'andrei.proprietar@izio.test',
          fromName: 'Andrei Proprietar',
          fromAvatar: 'https://i.pravatar.cc/150?img=33',
          toSlug: 'combo-pair',
          toName: 'Sara V.',
          status: 'postponed',
          sentAt: lastWeek.toISOString(),
          updatedAt: lastWeek.toISOString(),
          message:
            'Salut Sara! Am un 3 camere în Floreasca pentru un grup ca al vostru. Spațios, 2 băi.',
          contactEmail: 'andrei.proprietar@izio.test',
          contactPhone: '+40 712 345 678',
          listingPreview: {
            image:
              'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&q=80&w=400',
            title: 'Apartament 3 camere Floreasca',
            priceLabel: '780 €',
            locationLabel: 'Floreasca, București',
            url: 'https://izio.ro/anunt/ap-3-floreasca',
          },
        },
        {
          id: 'offer_andrei_stefan',
          fromEmail: 'andrei.proprietar@izio.test',
          fromName: 'Andrei Proprietar',
          fromAvatar: 'https://i.pravatar.cc/150?img=33',
          toSlug: 'stefan-r',
          toName: 'Stefan R.',
          status: 'rejected',
          sentAt: twoWeeksAgo.toISOString(),
          updatedAt: lastWeek.toISOString(),
          message:
            'Bună Ștefan, apartamentul meu din Cotroceni se potrivește bugetului tău.',
          contactEmail: 'andrei.proprietar@izio.test',
          contactPhone: '+40 712 345 678',
          rejectNote: 'Am găsit deja ceva, mulțumesc!',
          listingPreview: {
            image:
              'https://images.unsplash.com/photo-1502672260266-1c1e52d15461?auto=format&fit=crop&q=80&w=400',
            title: 'Apartament 2 camere Cotroceni',
            priceLabel: '550 €',
            locationLabel: 'Cotroceni, București',
            url: 'https://izio.ro/anunt/ap-2-cotroceni',
          },
        },
        {
          id: 'offer_andrei_ana',
          fromEmail: 'andrei.proprietar@izio.test',
          fromName: 'Andrei Proprietar',
          fromAvatar: 'https://i.pravatar.cc/150?img=33',
          toSlug: 'ana-d',
          toName: 'Ana D.',
          status: 'new',
          sentAt: lastWeek.toISOString(),
          updatedAt: lastWeek.toISOString(),
          message:
            'Salut Ana! Am un studio modern în Băneasa, complet mobilat. Te-ar interesa?',
          contactEmail: 'andrei.proprietar@izio.test',
          contactPhone: '+40 712 345 678',
          listingPreview: {
            image:
              'https://images.unsplash.com/photo-1554995207-c18c203602cb?auto=format&fit=crop&q=80&w=400',
            title: 'Studio modern Băneasa',
            priceLabel: '480 €',
            locationLabel: 'Băneasa, București',
            url: 'https://izio.ro/anunt/studio-baneasa',
          },
        },
      ]
      const properties: SavedProperty[] = [
        {
          id: 'prop_andrei_cotroceni',
          ownerEmail: 'andrei.proprietar@izio.test',
          name: 'Apartament 2 camere Cotroceni',
          title: 'Apartament 2 camere Cotroceni',
          priceLabel: '550 €/lună',
          locationLabel: 'Cotroceni, București',
          thumbnail:
            'https://images.unsplash.com/photo-1502672260266-1c1e52d15461?auto=format&fit=crop&q=80&w=400',
          facilities: {
            location: ['Aproape de metrou', 'Aproape de parc'],
            essential: [
              'Centrală termică',
              'Mobilat complet',
              'Aer condiționat',
              'Mașină de spălat',
            ],
            preferred: ['Balcon / Terasă', 'Lift'],
          },
          isActive: true,
          createdAt: twoWeeksAgo.toISOString(),
        },
        {
          id: 'prop_andrei_floreasca',
          ownerEmail: 'andrei.proprietar@izio.test',
          name: 'Apartament 3 camere Floreasca',
          title: 'Apartament 3 camere Floreasca',
          priceLabel: '780 €/lună',
          locationLabel: 'Floreasca, București',
          thumbnail:
            'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&q=80&w=400',
          facilities: {
            location: ['Aproape de parc', 'Zonă liniștită'],
            essential: [
              'Centrală termică',
              'Mobilat complet',
              'Aer condiționat',
              'Parcare',
              'Lift',
            ],
            preferred: ['Balcon / Terasă', '2 băi'],
          },
          isActive: true,
          createdAt: twoWeeksAgo.toISOString(),
        },
        {
          id: 'prop_andrei_baneasa',
          ownerEmail: 'andrei.proprietar@izio.test',
          name: 'Studio modern Băneasa',
          title: 'Studio modern Băneasa',
          priceLabel: '480 €/lună',
          locationLabel: 'Băneasa, București',
          thumbnail:
            'https://images.unsplash.com/photo-1554995207-c18c203602cb?auto=format&fit=crop&q=80&w=400',
          facilities: {
            location: ['Aproape de pădure'],
            essential: [
              'Centrală termică',
              'Mobilat complet',
              'Aer condiționat',
            ],
            preferred: ['Balcon / Terasă'],
          },
          isActive: true,
          createdAt: lastWeek.toISOString(),
        },
        {
          id: 'prop_andrei_dorobanti',
          ownerEmail: 'andrei.proprietar@izio.test',
          name: 'Apartament 2 camere Dorobanți',
          title: 'Apartament 2 camere Dorobanți',
          priceLabel: '620 €/lună',
          locationLabel: 'Dorobanți, București',
          thumbnail:
            'https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&q=80&w=400',
          facilities: {
            location: ['Aproape de metrou', 'Zonă centrală'],
            essential: [
              'Centrală termică',
              'Mobilat complet',
              'Aer condiționat',
              'Mașină de spălat',
            ],
            preferred: ['Balcon / Terasă', 'Lift'],
          },
          isActive: false,
          rentalStatus: 'rented',
          rentedAt: lastWeek.toISOString(),
          rentedTenantSlug: 'maria-i',
          rentedTenantName: 'Maria I.',
          rentedOfferId: 'offer_andrei_succes',
          createdAt: twoWeeksAgo.toISOString(),
        },
      ]
      try {
        localStorage.setItem(OFFERS_KEY, JSON.stringify(offers))
        localStorage.setItem(
          SAVED_PROPERTIES_KEY,
          JSON.stringify({
            'andrei.proprietar@izio.test': properties,
          }),
        )
      } catch (e) {
        console.error('Failed to seed Andrei Proprietar demo', e)
      }
      return
    }

    case 'maria.agent@izio.test': {
      // L9.S1 — Maria Agent demo. 3 active properties + 1 rented + 5
      // outgoing offers spread across statuses and properties.
      const offers: StoredOffer[] = [
        {
          id: 'offer_maria_succes',
          fromEmail: 'maria.agent@izio.test',
          fromName: 'Maria Agent',
          fromAvatar: 'https://i.pravatar.cc/150?img=48',
          toSlug: 'olivia-p',
          toName: 'Olivia P.',
          status: 'succes',
          sentAt: twoWeeksAgo.toISOString(),
          updatedAt: lastWeek.toISOString(),
          succesAt: lastWeek.toISOString(),
          message:
            'Bună Olivia! Am un 2 camere în Titan, recent renovat. S-ar potrivi cu voi.',
          contactEmail: 'maria.agent@izio.test',
          contactPhone: '+40 744 100 200',
          listingPreview: {
            image:
              'https://images.unsplash.com/photo-1484154218962-a197022b5858?auto=format&fit=crop&q=80&w=400',
            title: 'Apartament 2 camere Titan',
            priceLabel: '520 €',
            locationLabel: 'Titan, București',
            url: 'https://izio.ro/anunt/ap-2-titan',
          },
        },
        {
          id: 'offer_maria_andrei_p',
          fromEmail: 'maria.agent@izio.test',
          fromName: 'Maria Agent',
          fromAvatar: 'https://i.pravatar.cc/150?img=48',
          toSlug: 'andrei-popescu',
          toName: 'Andrei P.',
          status: 'succes',
          sentAt: lastWeek.toISOString(),
          updatedAt: now.toISOString(),
          succesAt: now.toISOString(),
          propertyId: 'prop_maria_aviatorilor',
          message:
            'Salut Andrei! Am un 3 camere în Aviatorilor, complet mobilat, parcare inclusă.',
          contactEmail: 'maria.agent@izio.test',
          contactPhone: '+40 744 100 200',
          listingPreview: {
            image:
              'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&q=80&w=400',
            title: 'Apartament 3 camere Aviatorilor',
            priceLabel: '850 €',
            locationLabel: 'Aviatorilor, București',
            url: 'https://izio.ro/anunt/ap-3-aviatorilor',
          },
        },
        {
          id: 'offer_maria_sara',
          fromEmail: 'maria.agent@izio.test',
          fromName: 'Maria Agent',
          fromAvatar: 'https://i.pravatar.cc/150?img=48',
          toSlug: 'combo-pair',
          toName: 'Sara V.',
          status: 'succes',
          sentAt: lastWeek.toISOString(),
          updatedAt: now.toISOString(),
          succesAt: now.toISOString(),
          propertyId: 'prop_maria_primaverii',
          message:
            'Bună Sara! Am un penthouse în Primăverii, perfect pentru un grup ca al vostru. 3 camere, 2 băi.',
          contactEmail: 'maria.agent@izio.test',
          contactPhone: '+40 744 100 200',
          acceptNote: 'Sună interesant, hai să vorbim mâine pe Whatsapp.',
          listingPreview: {
            image:
              'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&q=80&w=400',
            title: 'Penthouse Primăverii',
            priceLabel: '950 €',
            locationLabel: 'Primăverii, București',
            url: 'https://izio.ro/anunt/penthouse-primaverii',
          },
        },
        {
          id: 'offer_maria_maria_i',
          fromEmail: 'maria.agent@izio.test',
          fromName: 'Maria Agent',
          fromAvatar: 'https://i.pravatar.cc/150?img=48',
          toSlug: 'maria-i',
          toName: 'Maria I.',
          status: 'postponed',
          sentAt: lastWeek.toISOString(),
          updatedAt: lastWeek.toISOString(),
          message:
            'Bună Maria! Am o garsonieră în Dorobanți, compactă și super luminoasă.',
          contactEmail: 'maria.agent@izio.test',
          contactPhone: '+40 744 100 200',
          listingPreview: {
            image:
              'https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&q=80&w=400',
            title: 'Garsonieră Dorobanți',
            priceLabel: '480 €',
            locationLabel: 'Dorobanți, București',
            url: 'https://izio.ro/anunt/garsoniera-dorobanti',
          },
        },
        {
          id: 'offer_maria_dan',
          fromEmail: 'maria.agent@izio.test',
          fromName: 'Maria Agent',
          fromAvatar: 'https://i.pravatar.cc/150?img=48',
          toSlug: 'dan-c',
          toName: 'Dan C.',
          status: 'rejected',
          sentAt: twoWeeksAgo.toISOString(),
          updatedAt: lastWeek.toISOString(),
          message:
            'Bună Dan! Am un 3 camere în Aviatorilor care s-ar potrivi cu cerințele tale.',
          contactEmail: 'maria.agent@izio.test',
          contactPhone: '+40 744 100 200',
          rejectNote: 'Mulțumesc, dar am ales altceva.',
          listingPreview: {
            image:
              'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&q=80&w=400',
            title: 'Apartament 3 camere Aviatorilor',
            priceLabel: '850 €',
            locationLabel: 'Aviatorilor, București',
            url: 'https://izio.ro/anunt/ap-3-aviatorilor',
          },
        },
      ]
      const properties: SavedProperty[] = [
        {
          id: 'prop_maria_aviatorilor',
          ownerEmail: 'maria.agent@izio.test',
          name: 'Apartament 3 camere Aviatorilor',
          title: 'Apartament 3 camere Aviatorilor',
          priceLabel: '850 €/lună',
          locationLabel: 'Aviatorilor, București',
          thumbnail:
            'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&q=80&w=400',
          facilities: {
            location: ['Aproape de metrou', 'Zonă liniștită'],
            essential: [
              'Centrală termică',
              'Mobilat complet',
              'Aer condiționat',
              'Parcare',
              'Lift',
            ],
            preferred: ['Balcon / Terasă', 'Mașină de spălat vase'],
          },
          isActive: true,
          createdAt: twoWeeksAgo.toISOString(),
        },
        {
          id: 'prop_maria_dorobanti',
          ownerEmail: 'maria.agent@izio.test',
          name: 'Garsonieră Dorobanți',
          title: 'Garsonieră Dorobanți',
          priceLabel: '480 €/lună',
          locationLabel: 'Dorobanți, București',
          thumbnail:
            'https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&q=80&w=400',
          facilities: {
            location: ['Aproape de metrou'],
            essential: ['Centrală termică', 'Mobilat complet'],
            preferred: ['Lift'],
          },
          isActive: true,
          createdAt: lastWeek.toISOString(),
        },
        {
          id: 'prop_maria_primaverii',
          ownerEmail: 'maria.agent@izio.test',
          name: 'Penthouse Primăverii',
          title: 'Penthouse Primăverii',
          priceLabel: '950 €/lună',
          locationLabel: 'Primăverii, București',
          thumbnail:
            'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&q=80&w=400',
          facilities: {
            location: ['Zonă centrală', 'Aproape de parc'],
            essential: [
              'Centrală termică',
              'Mobilat complet',
              'Aer condiționat',
              'Parcare',
              'Lift',
              'Mașină de spălat vase',
            ],
            preferred: ['Balcon / Terasă', 'Vedere panoramică'],
          },
          isActive: true,
          createdAt: lastWeek.toISOString(),
        },
        {
          id: 'prop_maria_titan',
          ownerEmail: 'maria.agent@izio.test',
          name: 'Apartament 2 camere Titan',
          title: 'Apartament 2 camere Titan',
          priceLabel: '520 €/lună',
          locationLabel: 'Titan, București',
          thumbnail:
            'https://images.unsplash.com/photo-1484154218962-a197022b5858?auto=format&fit=crop&q=80&w=400',
          facilities: {
            location: ['Aproape de metrou', 'Aproape de parc'],
            essential: [
              'Centrală termică',
              'Mobilat complet',
              'Aer condiționat',
            ],
            preferred: ['Balcon / Terasă', 'Lift'],
          },
          isActive: false,
          rentalStatus: 'rented',
          rentedAt: lastWeek.toISOString(),
          rentedTenantSlug: 'olivia-p',
          rentedTenantName: 'Olivia P.',
          rentedOfferId: 'offer_maria_succes',
          createdAt: twoWeeksAgo.toISOString(),
        },
      ]
      try {
        localStorage.setItem(OFFERS_KEY, JSON.stringify(offers))
        localStorage.setItem(
          SAVED_PROPERTIES_KEY,
          JSON.stringify({
            'maria.agent@izio.test': properties,
          }),
        )
      } catch (e) {
        console.error('Failed to seed Maria Agent demo', e)
      }
      return
    }

    default:
      // Clean slate for everyone else
      return
  }
}
